import { useRef, useState, useCallback } from "react";
import SiteImageGallery from "@/components/feature/SiteImageGallery";
import { uploadFileToCloud } from "@/utils/cloudImageUpload";

interface AdminImageUploadProps {
  src: string;
  alt: string;
  onReplace: (url: string) => void;
  className?: string;
  imgClassName?: string;
  isDark?: boolean;
  objectPosition?: string;
  onPositionChange?: (pos: string) => void;
  /** Optional: separate mobile position */
  mobileObjectPosition?: string;
  onMobilePositionChange?: (pos: string) => void;
  /** Optional: delete handler. When provided, shows a delete button in the overlay. */
  onDelete?: () => void;
}

/**
 * Wraps an image with an admin-only overlay.
 * Hover to see options: upload, gallery, desktop position, mobile position.
 * Desktop and mobile positions are fully independent.
 * Frame feature has been removed.
 */
export default function AdminImageUpload({
  src,
  alt,
  onReplace,
  className = "",
  imgClassName = "w-full h-full object-cover object-top",
  isDark = false,
  objectPosition = "50% 50%",
  onPositionChange,
  mobileObjectPosition,
  onMobilePositionChange,
  onDelete,
}: AdminImageUploadProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [showGallery, setShowGallery] = useState(false);
  const [showPositionPicker, setShowPositionPicker] = useState(false);
  const [positionMode, setPositionMode] = useState<"desktop" | "mobile">("desktop");

  // ── Position picker state ──────────────────────────────────────────────────
  const parsePos = (raw: string): { x: number; y: number } => {
    const named: Record<string, number> = {
      left: 0, center: 50, right: 100, top: 0, bottom: 100,
    };
    const parts = raw.trim().split(/\s+/);
    if (parts.length === 2) {
      const xRaw = parts[0];
      const yRaw = parts[1];
      const x = xRaw.endsWith("%") ? parseFloat(xRaw) : (named[xRaw] ?? 50);
      const y = yRaw.endsWith("%") ? parseFloat(yRaw) : (named[yRaw] ?? 50);
      return { x: isNaN(x) ? 50 : x, y: isNaN(y) ? 50 : y };
    }
    return { x: 50, y: 50 };
  };

  // Keep desktop and mobile draft positions SEPARATE — never sync them
  const [desktopDraft, setDesktopDraft] = useState<{ x: number; y: number }>(() => parsePos(objectPosition));
  const [mobileDraft, setMobileDraft] = useState<{ x: number; y: number }>(() =>
    parsePos(mobileObjectPosition ?? objectPosition)
  );

  const dragRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  const posToStyle = (pos: { x: number; y: number }) => `${pos.x}% ${pos.y}%`;

  const handleDragArea = useCallback((e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const el = dragRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let clientX: number;
    let clientY: number;
    if ("touches" in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
    const rounded = { x: Math.round(x), y: Math.round(y) };
    if (positionMode === "mobile") {
      setMobileDraft(rounded);
    } else {
      setDesktopDraft(rounded);
    }
  }, [positionMode]);

  const openPositionPicker = (mode: "desktop" | "mobile" = "desktop") => {
    setPositionMode(mode);
    // Load the correct draft for the mode being opened — never cross-contaminate
    if (mode === "mobile") {
      setMobileDraft(parsePos(mobileObjectPosition ?? objectPosition));
    } else {
      setDesktopDraft(parsePos(objectPosition));
    }
    setShowPositionPicker(true);
  };

  const applyPosition = () => {
    if (positionMode === "mobile" && onMobilePositionChange) {
      onMobilePositionChange(posToStyle(mobileDraft));
    } else if (positionMode === "desktop" && onPositionChange) {
      onPositionChange(posToStyle(desktopDraft));
    }
    setShowPositionPicker(false);
  };

  const currentDraft = positionMode === "mobile" ? mobileDraft : desktopDraft;

  // ── File upload ────────────────────────────────────────────────────────────
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploading(true);
    setUploadError(null);
    try {
      const url = await uploadFileToCloud(file);
      onReplace(url);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setUploadError(msg);
      console.error("Upload error:", msg);
    } finally {
      setUploading(false);
    }
  };

  const imgStyle: React.CSSProperties = {
    objectPosition: objectPosition || "50% 50%",
  };

  // Determine which position buttons to show
  const showDesktopPos = !!onPositionChange;
  const showMobilePos = !!onMobilePositionChange;

  return (
    <>
      <div className={`group relative ${className}`} title="Replace image">
        {src ? (
          <img src={src} alt={alt} className={imgClassName} style={imgStyle} />
        ) : (
          <div
            className={`w-full h-full flex flex-col items-center justify-center border-2 border-dashed rounded-2xl cursor-pointer transition-colors ${
              isDark
                ? "border-gray-600 bg-gray-800/50 hover:border-pink-400 hover:bg-gray-700/50"
                : "border-gray-300 bg-gray-50 hover:border-pink-400 hover:bg-pink-50/50"
            }`}
            onClick={() => fileRef.current?.click()}
          >
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-pink-100 mb-3">
              <i className="ri-image-add-line text-pink-500 text-xl"></i>
            </div>
            <span className={`text-sm font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              Add Photo
            </span>
            <span className={`text-xs mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              Click to upload or browse gallery
            </span>
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center z-20">
            <i className="ri-loader-4-line animate-spin text-white text-2xl"></i>
            <span className="text-white text-xs font-bold mt-2">Uploading...</span>
          </div>
        )}
        {uploadError && (
          <div className="absolute inset-0 bg-red-900/70 flex flex-col items-center justify-center z-20 p-4">
            <i className="ri-error-warning-line text-red-300 text-2xl"></i>
            <span className="text-red-100 text-xs font-bold mt-2 text-center">Upload failed</span>
            <span className="text-red-200 text-[10px] mt-1 text-center max-w-[200px]">{uploadError}</span>
            <button
              onClick={(e) => { e.stopPropagation(); setUploadError(null); }}
              className="mt-2 text-[10px] text-white bg-red-500 hover:bg-red-600 px-2 py-1 rounded-full cursor-pointer"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Hover overlay with controls */}
        {src && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center gap-2 flex-wrap p-2 z-10">
            {/* Gallery button */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowGallery(true); }}
              className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-1 text-white cursor-pointer"
              title="Pick from gallery"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-pink-500 rounded-full">
                <i className="ri-gallery-line text-lg"></i>
              </div>
              <span className="text-[10px] font-bold bg-black/60 px-2 py-0.5 rounded-full whitespace-nowrap">Gallery</span>
            </button>

            {/* Upload button */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
              className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-1 text-white cursor-pointer"
              title="Upload from device"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-green-500 rounded-full">
                <i className="ri-upload-2-line text-lg"></i>
              </div>
              <span className="text-[10px] font-bold bg-black/60 px-2 py-0.5 rounded-full whitespace-nowrap">Upload</span>
            </button>

            {/* Desktop position button */}
            {showDesktopPos && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openPositionPicker("desktop"); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-1 text-white cursor-pointer"
                title="Adjust desktop photo position"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-amber-500 rounded-full">
                  <i className="ri-computer-line text-lg"></i>
                </div>
                <span className="text-[10px] font-bold bg-black/60 px-2 py-0.5 rounded-full whitespace-nowrap">Desktop Pos</span>
              </button>
            )}

            {/* Mobile position button */}
            {showMobilePos && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openPositionPicker("mobile"); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-1 text-white cursor-pointer"
                title="Adjust mobile photo position"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-sky-500 rounded-full">
                  <i className="ri-smartphone-line text-lg"></i>
                </div>
                <span className="text-[10px] font-bold bg-black/60 px-2 py-0.5 rounded-full whitespace-nowrap">Mobile Pos</span>
              </button>
            )}

            {/* Delete button */}
            {onDelete && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-1 text-white cursor-pointer"
                title="Remove image"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-red-500 rounded-full">
                  <i className="ri-delete-bin-line text-lg"></i>
                </div>
                <span className="text-[10px] font-bold bg-black/60 px-2 py-0.5 rounded-full whitespace-nowrap">Delete</span>
              </button>
            )}
          </div>
        )}

        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>

      {showGallery && (
        <SiteImageGallery
          isDark={isDark}
          title="Replace Image"
          onSelect={(url) => { onReplace(url); }}
          onClose={() => setShowGallery(false)}
        />
      )}

      {/* ── Drag-to-position modal ─────────────────────────────────────────── */}
      {showPositionPicker && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setShowPositionPicker(false)}
        >
          <div
            className={`w-full max-w-lg rounded-3xl border overflow-hidden ${isDark ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200"}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`px-6 py-4 border-b ${isDark ? "border-gray-700" : "border-gray-100"}`}>
              <div className="flex items-center gap-3 mb-1">
                <h3 className={`font-bold text-base ${isDark ? "text-white" : "text-gray-900"}`}>
                  {positionMode === "mobile" ? (
                    <><i className="ri-smartphone-line text-sky-500 mr-1.5"></i>Mobile Photo Position</>
                  ) : (
                    <><i className="ri-computer-line text-amber-500 mr-1.5"></i>Desktop Photo Position</>
                  )}
                </h3>
                {/* Mode switcher — only show if both are available */}
                {showMobilePos && showDesktopPos && (
                  <div className="ml-auto flex items-center gap-1 bg-gray-100 rounded-full p-0.5">
                    <button
                      onClick={() => {
                        setPositionMode("desktop");
                        setDesktopDraft(parsePos(objectPosition));
                      }}
                      className={`text-xs font-bold px-3 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${positionMode === "desktop" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`}
                    >
                      <i className="ri-computer-line mr-1"></i>Desktop
                    </button>
                    <button
                      onClick={() => {
                        setPositionMode("mobile");
                        setMobileDraft(parsePos(mobileObjectPosition ?? objectPosition));
                      }}
                      className={`text-xs font-bold px-3 py-1 rounded-full cursor-pointer whitespace-nowrap transition-colors ${positionMode === "mobile" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`}
                    >
                      <i className="ri-smartphone-line mr-1"></i>Mobile
                    </button>
                  </div>
                )}
              </div>
              <p className={`text-xs mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Drag anywhere on the image to set the focal point. Desktop and mobile positions are saved independently.
              </p>
            </div>

            {/* Drag area */}
            <div className="p-4">
              <div
                ref={dragRef}
                className="relative w-full rounded-2xl overflow-hidden cursor-crosshair select-none"
                style={{ aspectRatio: "16/9" }}
                onMouseDown={(e) => {
                  isDragging.current = true;
                  handleDragArea(e);
                }}
                onMouseMove={(e) => {
                  if (isDragging.current) handleDragArea(e);
                }}
                onMouseUp={() => { isDragging.current = false; }}
                onMouseLeave={() => { isDragging.current = false; }}
                onTouchStart={(e) => {
                  isDragging.current = true;
                  handleDragArea(e);
                }}
                onTouchMove={(e) => {
                  if (isDragging.current) handleDragArea(e);
                }}
                onTouchEnd={() => { isDragging.current = false; }}
              >
                <img
                  src={src}
                  alt={alt}
                  className="w-full h-full object-cover pointer-events-none"
                  style={{ objectPosition: posToStyle(currentDraft) }}
                  draggable={false}
                />
                <div className="absolute inset-0 bg-black/20 pointer-events-none"></div>
                <div className="absolute inset-0 pointer-events-none" style={{
                  backgroundImage: "linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)",
                  backgroundSize: "33.33% 33.33%",
                }}></div>
                {/* Crosshair dot */}
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: `${currentDraft.x}%`,
                    top: `${currentDraft.y}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <div className="w-8 h-8 rounded-full border-2 border-white/80 flex items-center justify-center">
                    <div className={`w-2.5 h-2.5 rounded-full border border-white ${positionMode === "mobile" ? "bg-sky-400" : "bg-amber-400"}`}></div>
                  </div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-px bg-white/60"></div>
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-12 bg-white/60"></div>
                </div>
              </div>
              <p className={`text-center text-xs mt-2 font-mono ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                {positionMode === "mobile" ? "Mobile" : "Desktop"} focal point: {currentDraft.x}% {currentDraft.y}%
              </p>
            </div>

            {/* Footer buttons */}
            <div className={`px-4 py-4 flex gap-2 border-t ${isDark ? "border-gray-700" : "border-gray-100"}`}>
              <button
                onClick={applyPosition}
                className={`flex-1 text-white font-bold py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${positionMode === "mobile" ? "bg-sky-500 hover:bg-sky-600" : "bg-amber-500 hover:bg-amber-600"}`}
              >
                <i className="ri-check-line mr-1"></i>Apply {positionMode === "mobile" ? "Mobile" : "Desktop"} Position
              </button>
              <button
                onClick={() => setShowPositionPicker(false)}
                className={`flex-1 border font-semibold py-2.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${isDark ? "border-gray-600 text-gray-300 hover:border-gray-400" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}